import React, { useMemo, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, Link, Radio, RadioGroup, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField,
  Tooltip, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { RenderRow } from './types';

type SortKey = 'renderedAt' | 'chart' | 'chartWeek' | 'orientation' | 'language';

interface HistoryTableProps {
  renders: RenderRow[];
  onOverride: (id: number, billable: boolean | null, note: string) => Promise<void>;
}

const PT_FORMAT: Intl.DateTimeFormatOptions = {
  timeZone: 'America/Los_Angeles',
  year: 'numeric', month: 'short', day: 'numeric',
  hour: 'numeric', minute: '2-digit',
};

function formatPt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-US', PT_FORMAT);
}

// chartWeek is YYYY-MM-DD when the server could parse it, else raw text.
export function formatChartWeek(week: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(week)) return week;
  const d = new Date(`${week}T12:00:00`);
  return isNaN(d.getTime()) ? week : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export const HistoryTable: React.FC<HistoryTableProps> = ({ renders, onOverride }) => {
  const [sortBy, setSortBy] = useState<SortKey>('renderedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [editing, setEditing] = useState<RenderRow | null>(null);
  const [editMode, setEditMode] = useState<'auto' | 'billable' | 'notBillable'>('auto');
  const [editNote, setEditNote] = useState('');
  const [saving, setSaving] = useState(false);

  const sorted = useMemo(() => {
    const dir = sortOrder === 'asc' ? 1 : -1;
    return [...renders].sort((a, b) => {
      const av = a[sortBy] || '';
      const bv = b[sortBy] || '';
      return av < bv ? -dir : av > bv ? dir : 0;
    });
  }, [renders, sortBy, sortOrder]);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(key); setSortOrder(key === 'renderedAt' ? 'desc' : 'asc'); }
  };

  const openEditor = (r: RenderRow) => {
    setEditing(r);
    setEditMode(r.overridden ? (r.billable ? 'billable' : 'notBillable') : 'auto');
    setEditNote(r.overrideNote || '');
  };

  const saveOverride = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const billable = editMode === 'auto' ? null : editMode === 'billable';
      await onOverride(editing.id, billable, editNote);
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const headerCell = (key: SortKey, label: string) => (
    <TableCell sortDirection={sortBy === key ? sortOrder : false}>
      <TableSortLabel
        active={sortBy === key}
        direction={sortBy === key ? sortOrder : 'asc'}
        onClick={() => handleSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {headerCell('renderedAt', 'Rendered (PT)')}
              {headerCell('chart', 'Chart')}
              {headerCell('chartWeek', 'Chart week')}
              {headerCell('orientation', 'Orientation')}
              {headerCell('language', 'Language')}
              <TableCell>Environment</TableCell>
              <TableCell>Billing</TableCell>
              <TableCell align="right">Video</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No renders in this range.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {sorted.map((r) => (
              <TableRow key={r.id} sx={{ opacity: r.billable ? 1 : 0.55 }}>
                <TableCell>{formatPt(r.renderedAt)}</TableCell>
                <TableCell>{r.chart}</TableCell>
                <TableCell>{formatChartWeek(r.chartWeek)}</TableCell>
                <TableCell>{r.orientation}</TableCell>
                <TableCell>{(r.language || 'en').toUpperCase()}</TableCell>
                <TableCell>
                  {r.environment || (
                    <Tooltip title="Rendered before environments were recorded (Oct 30, 2026)">
                      <span>—</span>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {r.billable ? (
                      <Chip size="small" color="success" label="Billable" />
                    ) : (
                      <Chip size="small" label={r.isRerun ? `Rerun (run #${r.runNumber})` : 'Not billed'} sx={{ bgcolor: '#fff3e0', color: '#a15c00' }} />
                    )}
                    {r.overridden && (
                      <Tooltip title={r.overrideNote ? `Manual override: ${r.overrideNote}` : 'Manual override'}>
                        <Chip size="small" variant="outlined" label="Override" />
                      </Tooltip>
                    )}
                    {r.fileDeleted && (
                      <Tooltip title="The video file was deleted (old copy cleaned up, or deleted in the Output Manager). The render still counts for billing.">
                        <Chip size="small" variant="outlined" label="File deleted" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {r.s3Link && !r.fileDeleted && (
                    <Link href={r.s3Link} target="_blank" rel="noopener noreferrer">
                      <OpenInNewIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
                    </Link>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit billing status">
                    <IconButton size="small" onClick={() => openEditor(r)}>
                      <EditIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Billing status</DialogTitle>
        <DialogContent>
          {editing && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {editing.chart} — {formatChartWeek(editing.chartWeek)} — {editing.orientation} — {(editing.language || 'en').toUpperCase()}
            </Typography>
          )}
          <RadioGroup value={editMode} onChange={(e) => setEditMode(e.target.value as typeof editMode)}>
            <FormControlLabel
              value="auto"
              control={<Radio size="small" />}
              label={`Automatic${editing ? (editing.runNumber === 1 ? ' (first run → billable)' : ` (run #${editing.runNumber} → rerun)`) : ''}`}
            />
            <FormControlLabel value="billable" control={<Radio size="small" />} label="Billable" />
            <FormControlLabel value="notBillable" control={<Radio size="small" />} label="Not billable" />
          </RadioGroup>
          {editMode !== 'auto' && (
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 1.5 }}
              label="Note (why the override?)"
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveOverride} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
